// System-readiness panel for the admin console: reads server config + provider
// keys and lists actionable "what needs to be done" items (dormant features,
// missing keys, misconfig). Read-only — keys live in the box's .env.
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  getReadiness,
  type ReadinessItem,
  type ReadinessStatus,
} from "@/api/admin-readiness";
import { Alert, Spinner, cx } from "@/components/ui";

const STATUS_ORDER: Record<ReadinessStatus, number> = {
  error: 0,
  action_needed: 1,
  info: 2,
  ready: 3,
};

const STATUS_PILL: Record<ReadinessStatus, string> = {
  ready: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  action_needed: "bg-amber-50 text-amber-700 ring-amber-200",
  error: "bg-red-50 text-red-700 ring-red-200",
  info: "bg-black/5 text-ink-soft ring-black/10",
};

const STATUS_LABEL: Record<ReadinessStatus, string> = {
  ready: "Ready",
  action_needed: "Action needed",
  error: "Error",
  info: "Info",
};

const STATUS_DOT: Record<ReadinessStatus, string> = {
  ready: "bg-emerald-500",
  action_needed: "bg-amber-500",
  error: "bg-red-500",
  info: "bg-slate-400",
};

export function AdminReadinessSection() {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ["admin-readiness"], queryFn: getReadiness });

  const grouped = groupByCategory(q.data?.items ?? []);

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-ink">{t("System readiness")}</h2>
          <p className="mt-1 text-sm text-ink-faint">
            {t("What's live, what's dormant, and what to do about it.")}
          </p>
        </div>
        {q.data ? (
          <span
            className={cx(
              "shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ring-1",
              q.data.action_count === 0
                ? STATUS_PILL.ready
                : STATUS_PILL.action_needed,
            )}
          >
            {q.data.action_count === 0
              ? t("All clear")
              : t("{{count}} need attention", { count: q.data.action_count })}
          </span>
        ) : null}
      </div>

      {q.isLoading ? (
        <div className="flex justify-center py-8 text-brand-600">
          <Spinner className="h-6 w-6" />
        </div>
      ) : q.isError || !q.data ? (
        <div className="mt-4">
          <Alert>{t("Couldn't load readiness. Try again.")}</Alert>
        </div>
      ) : (
        <div className="mt-4 space-y-5">
          {grouped.map(([category, items]) => (
            <div key={category}>
              <div className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
                {t(category)}
              </div>
              <ul className="mt-2 space-y-2">
                {items.map((it) => (
                  <ReadinessRow key={it.key} item={it} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReadinessRow({ item }: { item: ReadinessItem }) {
  const { t } = useTranslation();
  return (
    <li className="flex items-start gap-3 rounded-xl border border-black/5 bg-white p-3">
      <span className={cx("mt-1.5 h-2 w-2 shrink-0 rounded-full", STATUS_DOT[item.status])} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-ink">{item.label}</span>
          <span
            className={cx(
              "rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1",
              STATUS_PILL[item.status],
            )}
          >
            {t(STATUS_LABEL[item.status])}
          </span>
        </div>
        <p className="mt-0.5 text-sm text-ink-soft">{item.detail}</p>
        {item.action ? (
          <p className="mt-1 text-xs text-ink-faint">
            <span className="font-semibold">{t("Do")}: </span>
            {item.action}
          </p>
        ) : null}
      </div>
    </li>
  );
}

// Preserve first-seen category order (backend already emits a sensible order),
// and sort items within a category by severity.
function groupByCategory(items: ReadinessItem[]): [string, ReadinessItem[]][] {
  const order: string[] = [];
  const map = new Map<string, ReadinessItem[]>();
  for (const it of items) {
    if (!map.has(it.category)) {
      map.set(it.category, []);
      order.push(it.category);
    }
    map.get(it.category)!.push(it);
  }
  for (const list of map.values()) {
    list.sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
  }
  return order.map((c) => [c, map.get(c)!]);
}
